import { Typography } from 'antd';
import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow-down-icon.svg';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { StyledStack } from 'app/components/Stack';
import StatusComponent, { StatusState } from './StatusComponent';
import StyledGradientDivider from 'app/components/StyledGradientDivider';
import StyledPagination from 'app/components/StyledPagination';
import StyledTable from 'app/components/StyledTable';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import useFetchData from 'app/hooks/useFetchData';
import { Permission } from 'app/hooks/usePermission';
import useWindowSize from 'app/hooks/useWindowSize';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
import {
  cancelBookingSlotApi,
  getAvailableBookingSlotApi,
  getBookingSlotDetailApi,
  getBookingSlotListApi,
  getRestaurantListApi,
  getTicketShowListApi,
  getBookingUpdateStateApi,
} from 'app/services/bookingSlotApi';
import { getDataAgentApi } from 'app/services/checkTicketApi';
import _ from 'lodash';
import moment from 'moment';
import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import EmptyDataView from '../../CheckTickets/EmpyDataView';
import { convertVItoEN } from '../../CheckTickets/helper';
import RangeDatePickerCustom from '../../CheckTickets/RangeDatePickerCustom';
import StyledSearchInput from '../../components/StyledSearchInput';
import StyledSelect from '../../components/StyledSelect';
import BookingModal from './BookingModal';
import { MAIN_TABS } from '..';
export type IFilterBookingHistorySearch = {
  park?: number | string;
  orderDate?: string | moment.Moment;
  usageStartDate?: moment.Moment;
  usageEndDate?: moment.Moment;
  reservationStartDate?: moment.Moment;
  reservationEndDate?: moment.Moment;
  storeId?: string | number;
  product_id?: string | number;
  status?: number | string;
  ticketType?: string;
  reservationCode?: string;
  agentId?: string | number;
  limit?: number;
  page?: number;
};
export const DEFAULT_FILTER_SEARCH: IFilterBookingHistorySearch = {
  park: undefined,
  reservationStartDate: moment().startOf('month'),
  reservationEndDate: moment(),
  orderDate: undefined,
  usageStartDate: undefined,
  usageEndDate: undefined,
  product_id: undefined,
  status: undefined,
  ticketType: undefined,
  page: 1,
  limit: 20,
};
type IDataSelect = {
  value: string;
  label: string;
};
interface BookingHistoryProps {
  activeTab: MAIN_TABS;
}
const BookingHistory: FC<BookingHistoryProps> = ({ activeTab }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const { device } = useWindowSize();
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const [filter, setFilter] = useState<IFilterBookingHistorySearch>(
    DEFAULT_FILTER_SEARCH,
  );
  const [filterItemWith, setFilterItemWidth] = useState({
    small: 0,
    normal: 0,
  });
  const [dataProduct, setDataProduct] = useState<IDataSelect[]>([]);
  const [dataRestaurant, setDataRestaurant] = useState<IDataSelect[]>([]);
  const [isOpenBookingModal, setIsOpenBookingModal] = useState<boolean>(false);
  const [dataAgent, setDataAgent] = useState<any>([]);
  const [paginator, setPaginator] = useState<any>(undefined);
  const [dataBooking, setDataBooking] = useState<any>(undefined);
  const [infoData, setInfoData] = useState<any>(undefined);
  const translations = useSelector(selectTranslations);
  const infoUser: any = useSelector(selectInfoUser);
  const isSaleMng =
    infoUser?.role_code === Permission.ROLE_SALE_MANAGER ||
    infoUser?.role_code === Permission.ROLE_SALE_SHG;
  useEffect(() => {
    if (activeTab === MAIN_TABS.BOOKING_HISTORY) {
      onFetchRestaurantList();
      getDataAgent();
      getData(filter);
    }
  }, [activeTab]);
  const { onFetchData: onFetchRestaurantList } = useFetchData({
    actionApi: getRestaurantListApi,
    IIFE: false,
    onSuccess: response => {
      if (_.isEmpty(response)) return [];
      const resData = (response || []).map((item: any) => {
        return {
          label: item.name,
          value: `${item.id}`,
        };
      });
      const restaurantIds = response?.map(el => el?.restaurant?.id)?.join(',');
      if (restaurantIds) {
        onFetchProductList({ payloadData: { restaurantId: restaurantIds } });
      }
      setDataRestaurant(resData);
    },
  });
  const { onFetchData: onFetchProductList } = useFetchData({
    actionApi: getTicketShowListApi,
    IIFE: false,
    onSuccess: response => {
      if (_.isEmpty(response)) return [];
      const resData = (response || []).map((item: any) => {
        return {
          label: item.name,
          value: `${item.id}`,
        };
      });
      setDataProduct(resData);
    },
  });
  const {
    onFetchData: onFetchBookingSlotList,
    isLoading: isLoadingBookingSlotList,
  } = useFetchData({
    actionApi: getBookingSlotListApi,
    IIFE: false,
    onSuccess: response => {
      setPaginator(response?.paginator);
      setDataBooking(response?.data || []);
    },
  });
  const { onFetchData: onFetchBookingUpdateState } = useFetchData({
    actionApi: getBookingUpdateStateApi,
    IIFE: false,
  });
  const {
    onFetchData: onFetchBookingSlotDetail,
    isLoading: isLoadingBookingSlotDetail,
    result: resBookingSlotDetail,
  } = useFetchData({
    actionApi: getBookingSlotDetailApi,
    IIFE: false,
  });
  const {
    onFetchData: onFetchAvailableBookingSlot,
    isLoading: isLoadingAvailableBookingSlot,
    result: resAvailableBookingSlot,
  } = useFetchData({
    actionApi: getAvailableBookingSlotApi,
    IIFE: false,
  });
  const { onFetchData: onFetchCancelBookingSlot } = useFetchData({
    actionApi: cancelBookingSlotApi,
    IIFE: false,
    onSuccess: response => {
      getData(filter);
    },
  });
  const optionStatus = useMemo(() => {
    return [
      { value: 'All', label: translations?.['b2b.text.all'] },
      { value: '6', label: translations?.['b2b.txt.seated'] },
      { value: '2', label: translations?.['b2b.txt.confirmed'] },
      { value: '0', label: translations?.['b2b.option.cancelled'] },
      { value: '7', label: translations?.['b2b.txt.completed'] },
      { value: '5', label: translations?.['b2b.txt.ticket-expired'] },
      { value: '8', label: translations?.['b2b.txt.noShow'] },
    ];
  }, [translations]);
  useEffect(() => {
    if (
      !_.isEmpty(resBookingSlotDetail)
      //  &&
      // !_.isEmpty(resAvailableBookingSlot)
    ) {
      const {
        id,
        zone,
        ticketShow,
        arrivalDate,
        store,
        quantity,
        infantQuantity,
        note,
        representativeName,
        representativePhone,
        reservationCode,
        reservationId,
        country,
      } = resBookingSlotDetail;
      // const { numberOfUnused, numberOfUsed } = resAvailableBookingSlot;
      // const dataZoneTemp = !_.isEmpty(zone)
      //   ? [
      //       {
      //         id: zone?.zoneId,
      //         name: `Zone ${zone?.zoneId} (${zone.name})`,
      //         slot: numberOfUnused || 0,
      //       },
      //     ]
      //   : [];
      const ticketName = ticketShow?.name || '';
      // const totalAvailableSlot = dataZoneTemp.reduce(
      //   (acc, cur) => acc + cur.slot,
      //   0,
      // );
      const dataTemp = {
        ticketName,
        // totalAvailableSlot,
        // numberOfUsed: numberOfUsed || 0,
        // dataZone: dataZoneTemp,
        orderDate: arrivalDate,
        productId: ticketShow?.id,
        storeId: store?.storeId,
        quantity,
        infantQuantity,
        note,
        fullName: representativeName || '',
        phoneNumber: representativePhone || '',
        reservationCode,
        reservationId,
        referenceCode: id,
        customerNationality: country,
        zone: zone?.zoneId,
      };
      setInfoData(dataTemp);
      setIsOpenBookingModal(true);
    }
  }, [resBookingSlotDetail, resAvailableBookingSlot]);
  const getDataAgent = useCallback(async () => {
    try {
      const { data } = await getDataAgentApi({ notIncludeAllocatedDebt: '1' });
      if (!_.isEmpty(data?.data)) {
        const resAgents = data?.data.map((item: any) => {
          return {
            label: item.name,
            value: item.id,
          };
        });
        setDataAgent(resAgents);
      }
    } catch (error) {}
  }, [isSaleMng, translations]);
  useLayoutEffect(() => {
    const getFilterItemWidth = () => {
      if (!containerRef.current) return;
      const containerWidth = Math.floor(
        containerRef.current!.getBoundingClientRect().width,
      );
      setFilterItemWidth({
        small: (containerWidth! - 5 * 12 - 120) / 5,
        normal: (containerWidth! - 3 * 12) / 4,
      });
    };
    getFilterItemWidth();
    window.addEventListener('resize', getFilterItemWidth);
    return () => {
      window.removeEventListener('resize', getFilterItemWidth);
    };
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const getData = (param: IFilterBookingHistorySearch) => {
    const {
      reservationStartDate,
      reservationEndDate,
      usageStartDate,
      usageEndDate,
      product_id,
      reservationCode,
      ...rest
    } = param;
    const paramSubmit = {
      ...rest,
      createFrom: reservationStartDate
        ? reservationStartDate?.format('YYYY-MM-DD')
        : undefined,
      createTo: reservationEndDate
        ? reservationEndDate?.format('YYYY-MM-DD')
        : undefined,
      arrivalDateFrom: usageStartDate
        ? usageStartDate?.format('YYYY-MM-DD')
        : undefined,
      arrivalDateTo: usageEndDate
        ? usageEndDate?.format('YYYY-MM-DD')
        : undefined,
      ticketShowId: product_id || undefined,
      bookingId: reservationCode || undefined,
    };
    onFetchBookingUpdateState();
    onFetchBookingSlotList({ payloadData: paramSubmit });
  };
  const formatString = 'HH:mm:ss DD/MM/YYYY';
  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.txt.reservationCode',
      'b2b.txt.reservationDate',
      'b2b.txt.product-name',
      'b2b.txt.restaurant',
      'b2b.txt.zone',
      'b2b.txt.usageDate',
      'b2b.txt.ticketAmount',
      'b2b.txt.childrenFree',
      'b2b.txt.customerNationality',
      'b2b.form.tourNotes',
      'b2b.txt.representativeInformation',
      'b2b.txt.create-user',
      'b2b.txt.status',
      'b2b.txt.tableAction',
    ],
  });
  const columns = useMemo(
    () =>
      [
        {
          key: 'reservationCode',
          dataIndex: 'reservationCode',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.reservationCode'] : ''}
            </div>
          ),
          render: value => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.reservationCode'],
                }}
              >
                {value || ''}
              </div>
            );
          },
        },
        {
          key: 'reservationDate',
          dataIndex: 'reservationDate',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.reservationDate'] : ''}
            </div>
          ),
          render: (value, record) => {
            if (_.isNil(record?.createdAt))
              return (
                <div
                  style={{
                    minWidth:
                      titleColumnSizes?.['b2b.txt.reservationDate'] ?? '',
                  }}
                />
              );
            const [day, time] = String(record?.createdAt)?.trim()?.split(' ');
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.reservationDate'],
                }}
              >
                <Typography>{day}</Typography>
                <Typography>{time}</Typography>
              </div>
            );
          },
        },
        {
          key: 'productName',
          dataIndex: 'productName',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.product-name'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.product-name'],
                }}
              >
                <TooltipParagraph
                  ellipsis={{ rows: 1 }}
                  style={{
                    width: 140,
                    marginBottom: 0,
                    textAlign: 'center',
                  }}
                >
                  {record?.ticketShow?.name ?? ''}
                </TooltipParagraph>
              </div>
            );
          },
        },
        {
          key: 'restaurantName',
          dataIndex: 'restaurantName',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.restaurant'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.restaurant'],
                }}
              >
                <TooltipParagraph
                  ellipsis={{ rows: 1 }}
                  style={{
                    width: 140,
                    marginBottom: 0,
                  }}
                >
                  {record?.store?.name ?? ''}
                </TooltipParagraph>
              </div>
            );
          },
        },
        {
          key: 'zone',
          dataIndex: 'zone',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.zone'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.zone'],
                }}
              >
                <TooltipParagraph
                  ellipsis={{ rows: 1 }}
                  style={{
                    width: 140,
                    marginBottom: 0,
                    textAlign: 'left',
                  }}
                >
                  {record?.zone?.name ?? ''}
                </TooltipParagraph>
              </div>
            );
          },
        },
        {
          key: 'usageDate',
          dataIndex: 'usageDate',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.usageDate'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.usageDate'],
                }}
              >
                <Typography>{record?.arrivalDate || ''}</Typography>
              </div>
            );
          },
        },
        {
          key: 'ticketAmount',
          dataIndex: 'ticketAmount',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.ticketAmount'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.ticketAmount'],
                }}
              >
                <Typography>{record?.quantity ?? ''}</Typography>
              </div>
            );
          },
        },
        {
          key: 'childrenFree',
          dataIndex: 'childrenFree',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.childrenFree'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.childrenFree'],
                }}
              >
                <Typography>{record?.infantQuantity ?? ''}</Typography>
              </div>
            );
          },
        },
        {
          key: 'customerNationality',
          dataIndex: 'customerNationality',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.customerNationality'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <>
                <TooltipParagraph
                  ellipsis={{ rows: 1 }}
                  style={{
                    maxWidth: 200,
                    minWidth: titleColumnSizes['b2b.txt.customerNationality'],
                    marginBottom: 0,
                    textAlign: 'left',
                  }}
                >
                  {record?.country ?? ''}
                </TooltipParagraph>
              </>
            );
          },
        },
        {
          key: 'tourNotes',
          dataIndex: 'tourNotes',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.form.tourNotes'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <>
                <TooltipParagraph
                  ellipsis={{ rows: 1 }}
                  style={{
                    maxWidth: 200,
                    minWidth: titleColumnSizes['b2b.form.tourNotes'],
                    marginBottom: 0,
                    textAlign: 'left',
                  }}
                >
                  {record?.note ?? ''}
                </TooltipParagraph>
              </>
            );
          },
        },
        {
          key: 'representativeInformation',
          dataIndex: 'representativeInformation',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations
                ? translations['b2b.txt.representativeInformation']
                : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth:
                    titleColumnSizes['b2b.txt.representativeInformation'],
                }}
              >
                <TooltipParagraph
                  ellipsis={{ rows: 1 }}
                  style={{
                    width: 140,
                    marginBottom: 0,
                    textAlign: 'left',
                  }}
                >
                  {record?.representativeName ?? ''}
                </TooltipParagraph>
                <Typography>{record?.representativePhone ?? ''}</Typography>
              </div>
            );
          },
        },
        {
          key: 'createUser',
          dataIndex: 'createUser',
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.create-user'] : ''}
            </div>
          ),
          render: (_, record) => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  minWidth: titleColumnSizes['b2b.txt.create-user'],
                }}
              >
                {record?.agent?.name}
              </div>
            );
          },
        },
        {
          key: 'status',
          dataIndex: 'status',
          title: (
            <div>{translations ? translations['b2b.txt.status'] : ''}</div>
          ),
          render: (_, record) => {
            return (
              <div style={{ minWidth: titleColumnSizes['b2b.txt.status'] }}>
                <StatusComponent
                  status={String(record?.status) as StatusState}
                  label={record?.statusName}
                />
              </div>
            );
          },
        },
        {
          title: (
            <div style={{ textAlign: 'left' }}>
              {translations ? translations['b2b.txt.tableAction'] : ''}
            </div>
          ),
          key: 'tableAction',
          render: (_, record) => {
            const now = moment();
            const openTime = String(record.store?.openTime) ?? '16:00';
            const [hour, minute] = openTime.split(':');
            const cutoff = moment().set({
              hour: Number(hour),
              minute: Number(minute || 0),
              second: 0,
            });
            const isDisabled = record.arrivalDate
              ? (() => {
                  const arrival = moment(record.arrivalDate, 'DD/MM/YYYY');
                  if (arrival.isBefore(now, 'day')) return true;
                  if (arrival.isSame(now, 'day') && now.isAfter(cutoff))
                    return true;
                  return false;
                })()
              : true;
            const isNotOwner =
              String(infoUser?.username) !== String(record.agent?.username);
            return (
              <div
                style={{
                  display: 'flex',
                  minWidth: titleColumnSizes['b2b.txt.tableAction'],
                  gap: 10,
                }}
              >
                <ButtonAction
                  onClick={() => onClickCancel(record)}
                  disabled={
                    !record ||
                    isDisabled ||
                    String(record.status) !== '2' ||
                    isNotOwner
                  }
                >
                  {translations?.['b2b.btn.cancel']}
                </ButtonAction>
                <ButtonAction
                  primary
                  onClick={() => onClickEdit(record)}
                  disabled={
                    !record ||
                    isDisabled ||
                    String(record.status) !== '2' ||
                    isNotOwner
                  }
                >
                  {translations?.['b2b.btn.edit']}
                </ButtonAction>
              </div>
            );
          },
        },
      ].filter(item => typeof item !== 'boolean'),
    [titleColumnSizes, translations],
  );
  const onClickCancel = (data: any) => {
    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: translations?.['b2b.text.notify'] ?? '',
      content: translations?.['b2b.txt.confirmCancelReservation'],
      buttons: [
        {
          text: translations?.['b2b.txt.no'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.txt.yes'] ?? '',
          bg: 'var(--color-gradient)',
          color: 'white',
          onClick: () => {
            onCloseDialogNoti();
            onFetchCancelBookingSlot({
              payloadData: { referenceCode: data.id },
            });
          },
        },
      ],
      maskClosable: false,
      onCloseDialog: onCloseDialogNoti,
    });
  };
  const onClickEdit = (data: any) => {
    onFetchBookingSlotDetail({ payloadData: { id: data.id } });
    // onFetchAvailableBookingSlot({
    //   payloadData: {
    //     zoneId: data?.zone?.zoneId,
    //     arrivalDate: data?.arrivalDate
    //       ? moment(data?.arrivalDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).format(
    //           'YYYY-MM-DD',
    //         )
    //       : undefined,
    //   },
    // });
  };
  const onCloseBookingModal = (isCallApi?: boolean) => {
    setIsOpenBookingModal(false);
    setInfoData(undefined);
    if (isCallApi) {
      getData(filter);
    }
  };
  const handleChangePage = (page, sizePage) => {
    setFilter({ ...filter, page, limit: sizePage });
    getData({ ...filter, page, limit: sizePage });
  };
  const onClickSearch = () => {
    getData({ ...filter, page: 1, limit: 20 });
  };
  const handleChange = (
    name: keyof IFilterBookingHistorySearch,
    value: any,
  ) => {
    setFilter(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  return (
    <LoadingContainer
      loading={
        isLoadingBookingSlotList ||
        isLoadingBookingSlotDetail ||
        isLoadingAvailableBookingSlot
      }
    >
      <Title>{translations?.['b2b.txt.bookingHistory']}</Title>
      <StyledStack
        columnGap={'12px'}
        direction={{ lg: 'row', md: 'column' }}
        style={{ width: '100%', flexWrap: 'wrap' }}
        rowGap={'8px'}
        ref={containerRef}
      >
        <StyledStack
          style={{
            width: device === 'desktop' ? filterItemWith.normal : '100%',
          }}
        >
          <StyledSearchInput
            placeholder={
              translations ? translations['b2b.txt.reservationCode'] : ''
            }
            onChange={val =>
              setFilter({
                ...filter,
                reservationCode: val.target.value ?? '',
              })
            }
            value={filter?.reservationCode}
            width={'100%'}
            maxLength={256}
          />
        </StyledStack>
        <RangeDatePickerContainer
          style={{
            width: device === 'desktop' ? filterItemWith.normal : '100%',
          }}
        >
          <RangeDatePickerCustom
            placeholder={
              translations ? translations['b2b.txt.reservationDate'] : ''
            }
            startDateInit={filter?.reservationStartDate?.toDate()}
            endDateInit={filter?.reservationEndDate?.toDate()}
            syncFromProps
            onChange={value => {
              const [start, end] = value;
              if (!start) {
                setFilter({
                  ...filter,
                  reservationStartDate: undefined,
                  reservationEndDate: undefined,
                });
                return;
              }
              let endDate = end ?? start;
              if (moment(endDate).diff(moment(start), 'days') > 29) {
                endDate = moment(start).add(29, 'days').toDate();
              }
              setFilter({
                ...filter,
                reservationStartDate: moment(start),
                reservationEndDate: moment(endDate),
              });
            }}
            minDate={null}
          />
        </RangeDatePickerContainer>
        <StyledStack
          style={{
            width: device === 'desktop' ? filterItemWith.normal : '100%',
          }}
        >
          <StyledSelect
            filterOption={(input, option) =>
              (convertVItoEN(option?.label) ?? '').includes(
                convertVItoEN(input),
              )
            }
            showSearch
            isFocus
            value={filter.storeId}
            suffixIcon={<ArrowDownIcon />}
            placeholder={translations ? translations['b2b.txt.restaurant'] : ''}
            onChange={val => handleChange('storeId', val)}
            options={dataRestaurant}
            style={{ width: '100%' }}
            allowClear
          />
        </StyledStack>
        <StyledStack
          style={{
            width: device === 'desktop' ? filterItemWith.normal : '100%',
          }}
        >
          <StyledSelect
            filterOption={(input, option) =>
              (convertVItoEN(option?.label) ?? '').includes(
                convertVItoEN(input),
              )
            }
            showSearch
            isFocus
            value={filter.product_id}
            suffixIcon={<ArrowDownIcon />}
            placeholder={translations?.['b2b.booking.products']}
            onChange={val => handleChange('product_id', val)}
            options={dataProduct}
            style={{ width: '100%' }}
            allowClear
          />
        </StyledStack>
        <StyledStack
          style={{
            width: device === 'desktop' ? filterItemWith.normal : '100%',
          }}
        >
          <StyledSelect
            filterOption={(input, option) =>
              (convertVItoEN(option?.label) ?? '').includes(
                convertVItoEN(input),
              )
            }
            value={filter.status}
            suffixIcon={<ArrowDownIcon />}
            placeholder={translations ? translations['b2b.txt.status'] : ''}
            onChange={val => handleChange('status', val)}
            options={optionStatus}
            style={{ width: '100%' }}
            allowClear
          />
        </StyledStack>
        <RangeDatePickerContainer
          style={{
            width: device === 'desktop' ? filterItemWith.normal : '100%',
          }}
        >
          <RangeDatePickerCustom
            placeholder={translations ? translations['b2b.txt.usageDate'] : ''}
            startDateInit={filter?.usageStartDate?.toDate()}
            endDateInit={filter?.usageEndDate?.toDate()}
            syncFromProps
            onChange={value => {
              const [start, end] = value;
              if (!start) {
                setFilter({
                  ...filter,
                  usageStartDate: undefined,
                  usageEndDate: undefined,
                });
                return;
              }
              let endDate = end ?? start;
              if (moment(endDate).diff(moment(start), 'days') > 29) {
                endDate = moment(start).add(29, 'days').toDate();
              }
              setFilter({
                ...filter,
                usageStartDate: moment(start),
                usageEndDate: moment(endDate),
              });
            }}
            minDate={null}
            isClearable
          />
        </RangeDatePickerContainer>
        {isSaleMng && (
          <StyledStack
            style={{
              width: device === 'desktop' ? filterItemWith.normal : '100%',
            }}
          >
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              value={filter.park}
              suffixIcon={<ArrowDownIcon />}
              placeholder={translations ? translations['b2b.txt.executor'] : ''}
              onChange={val => handleChange('agentId', val)}
              options={dataAgent}
              style={{ width: '100%' }}
              allowClear
            />
          </StyledStack>
        )}
        <SearchButton onClick={onClickSearch}>
          {translations ? translations['b2c.txt.search'] : ''}
        </SearchButton>
      </StyledStack>
      <div style={{ marginTop: 20 }}>
        <StyledTable
          tableRef={tableRef}
          dataSource={dataBooking}
          columns={columns}
          tableProps={{
            pagination: false,
            scroll: {
              y: _.isEmpty(dataBooking) ? undefined : '50vh',
              x: _.isEmpty(dataBooking) ? 'max-content' : 'auto',
            },
            locale: {
              emptyText: (
                <EmptyDataView
                  message={
                    translations
                      ? translations['b2b.txt.noSlotsMatchSearch']
                      : ''
                  }
                />
              ),
            },
          }}
          styleSlots={{
            antTableContainer: {
              overflow: 'hidden',
            },
            antTableHeader: {
              border: 'none !important',
              borderRadius: '0 !important',
            },
            tableCSS: {
              borderTopLeftRadius: _.isEmpty(dataBooking) ? '' : '0 !important',
              borderTopRightRadius: _.isEmpty(dataBooking)
                ? ''
                : '0 !important',
            },
            antTableBody: {
              paddingBottom: 10,
            },
          }}
        />
      </div>
      {paginator && (
        <>
          <StyledGradientDivider style={{ marginTop: '40px' }} />
          <StyledPagination
            style={{
              marginTop: '40px',
              display: 'flex',
            }}
            showSizeChanger
            defaultCurrent={1}
            pageSizeOptions={['20', '50', '100']}
            current={filter.page}
            total={paginator.totalCount}
            pageSize={filter.limit}
            onChange={(page, pageSize) => {
              filter && handleChangePage(page, pageSize);
            }}
          />
          <StyledGradientDivider style={{ marginTop: '40px' }} />
        </>
      )}
      {isOpenBookingModal && !_.isEmpty(infoData) && (
        <BookingModal
          isShow={isOpenBookingModal}
          data={infoData}
          onCloseModal={onCloseBookingModal}
          typeModal="update"
          filter={filter}
        />
      )}
    </LoadingContainer>
  );
};
export default BookingHistory;
const Title = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
`;
const SearchButton = styled.button`
  height: 42px;
  padding: 0 24px;
  border: none;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #f8b117;
  color: #f8b117;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    background: #d4d4d4;
    cursor: not-allowed;
    opacity: 1;
  }
`;
const RangeDatePickerContainer = styled.div`
  width: 100%;
  .range-picker-container {
    width: 100%;
  }
  .react-datepicker__close-icon {
    right: 5px;
  }
  .date-picker {
    height: 44px;
  }
`;
const ButtonAction = styled.button<{ primary?: boolean }>`
  height: 42px;
  width: max-content;
  min-width: fit-content;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ primary }) => (primary ? 'var(--color-4)' : 'var(--color-4)')};
  background: ${({ primary }) =>
    primary ? 'var(--color-gradient)' : 'transparent'};
  color: ${({ primary }) => (primary ? '#fff' : 'var(--color-4)')};
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    background: #d4d4d4;
    border: 1px solid ${({ primary }) => (primary ? '#d4d4d4' : '#d4d4d4')};
    background: ${({ primary }) => (primary ? '#d4d4d4' : 'transparent')};
    color: ${({ primary }) => (primary ? '#fff' : '#d4d4d4')};
    cursor: not-allowed;
    opacity: 1;
  }
`;
